import React, { useCallback, useEffect, useState } from 'react'
import TextButton from 'LuxKit/Button/TextButton'
import LineShareAltIcon from 'LuxKit/Icons/line/LineShareAltIcon'
import * as Analytics from 'analytics/analytics'
import IconButton from 'LuxKit/Button/IconButton'
import type { Placement } from 'LuxKit/RichTooltip'
import RichTooltip from 'LuxKit/RichTooltip'
import { useIntl } from 'react-intl'

interface Props extends Omit<React.ComponentProps<typeof TextButton>, | 'kind' | 'children'> {
  iconOnly?: boolean
  /** When true, the share tooltip may show (once per session) after a delay */
  showToolTip?: boolean
  tooltipPlacement?: Placement
}

const SESSION_FLAG_KEY = 'le_share_tooltip_shown'

function ShareWithFriendsButton(props: Props) {
  const intl = useIntl()
  const { onClick, iconOnly, showToolTip, tooltipPlacement, ...rest } = props

  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false)

  const onShareClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e)
    Analytics.trackClientEvent({
      subject: 'share',
      action: 'pressed',
      category: 'offer_page',
      type: 'interaction',
    })
  }, [onClick])

  const onTooltipDismiss = useCallback(() => {
    setIsTooltipOpen(false)
  }, [])

  useEffect(() => {
    if (!showToolTip) return
    if (typeof window === 'undefined') return
    if (sessionStorage.getItem(SESSION_FLAG_KEY) === 'true') return

    const timerId = window.setTimeout(() => {
      setIsTooltipOpen(true)
      try { sessionStorage.setItem(SESSION_FLAG_KEY, 'true') } catch {}
    }, 1000 * 10) // 10 seconds

    return () => { window.clearTimeout(timerId) }
  }, [showToolTip])

  const button = iconOnly ? (
    <IconButton
      {...rest}
      size="medium"
      shape="circle"
      kind="secondary"
      variant="dark"
      onClick={onShareClick}
    >
      <LineShareAltIcon aria-hidden="true" />
    </IconButton>
  ) : (
    <TextButton
      kind="tertiary"
      {...rest}
      startIcon={<LineShareAltIcon aria-hidden="true" />}
      onClick={onShareClick}
    >
      {intl.formatMessage({
        id: 'kF4OS3QF5V',
        defaultMessage: 'Share',
        description: 'Button text for sharing offer',
      })}
    </TextButton>
  )

  return (
    <RichTooltip
      portaled
      placement={tooltipPlacement ?? 'top-end'}
      open={isTooltipOpen}
      onDismiss={onTooltipDismiss}
      description={intl.formatMessage({
        id: 'HthI9KW44W',
        defaultMessage: 'Share this offer with a friend and unlock discounts if they go on to purchase!',
        description: 'Tooltip description for share button',
      })}
    >
      {button}
    </RichTooltip>
  )
}

export default ShareWithFriendsButton
